import resolveResponseTimestamp from './resolveResponseTimestamp';

const BATCH_LIMIT = 450; // under Firestore's 500-op cap, with headroom

/**
 * Writes a build-import plan to Firestore in chunked batches. Structure docs are stamped
 * with the server `timestamp`; responses are stamped with the file's own date (parsed from
 * `timestampRaw`) so analytics date-range filters see them in the right period. Each
 * respondent's date is offset by its `sessionIndex` (ms) so submissions sharing one file
 * timestamp stay separate (the app groups submissions by millisecond Timestamp).
 *
 * Not unit-tested (Firestore writes) — logic lives in the pure builders.
 *
 * @param {object} plan - Output of buildImportPlan.
 * @param {object} deps
 * @param {object} deps.db - Firestore instance.
 * @param {*} deps.timestamp - Server timestamp value for structure docs.
 * @param {string} deps.researchDocId - Firestore docid of the Research (for the QuestionnaireID update).
 * @returns {Promise<{written: number}>}
 */
const executeImportPlan = async (plan, { db, timestamp, researchDocId }) => {
    /** @type {Array<{ref: object, data: object}>} */
    const ops = [];

    // Fallback base for responses without a parseable file date. One fixed
    // wall-clock moment per run — resolveResponseTimestamp offsets it per
    // respondent so submissions never share a millisecond (inbox #82).
    const runDate = new Date();

    const addCreate = (collection, data) => {
        const ref = db.collection(collection).doc();
        ops.push({ ref, data: { ...data, Timestamp: timestamp } });
    };

    if (plan.questionnaireToCreate) addCreate('Questionnaires', plan.questionnaireToCreate);
    plan.effectsToCreate.forEach((d) => addCreate('OutputEffects', d));
    plan.fieldsToCreate.forEach((d) => addCreate('QuestionnaireFields', d));
    plan.columnsToCreate.forEach((d) => addCreate('MatrixQuestionColumns', d));
    plan.rowsToCreate.forEach((d) => addCreate('MatrixQuestionRows', d));
    plan.personasToCreate.forEach((d) => addCreate('Personas', d));
    plan.personaDataToCreate.forEach((d) => addCreate('PersonaData', d));

    plan.responsesToWrite.forEach((resp) => {
        const { timestampRaw, sessionIndex, ...rest } = resp;
        const ref = db.collection('QuestionnairesResponses').doc();
        ops.push({
            ref,
            data: { ...rest, Timestamp: resolveResponseTimestamp(timestampRaw, sessionIndex, runDate) },
        });
    });

    // Research → questionnaire link (update, not create).
    if (plan.researchUpdate && researchDocId) {
        const ref = db.collection('Research').doc(researchDocId);
        ops.push({ ref, data: plan.researchUpdate, update: true });
    }

    for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
        const slice = ops.slice(i, i + BATCH_LIMIT);
        const batch = db.batch();
        slice.forEach(({ ref, data, update }) => {
            if (update) batch.update(ref, data);
            else batch.set(ref, data);
        });
        // eslint-disable-next-line no-await-in-loop
        await batch.commit();
    }

    return { written: ops.length };
};

export default executeImportPlan;
